import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { getStatusBadgeClasses } from '../lib/statusUtils';
import { toast } from 'sonner';

export const PolicyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [policy, setPolicy] = useState(null);
  const [commission, setCommission] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchPolicy();
    if (isAdmin) {
      fetchCommission();
    }
  }, [id, isAdmin]);

  const fetchPolicy = async () => {
    try {
      const response = await api.get(`/policies/${id}`);
      setPolicy(response.data);
    } catch (error) {
      toast.error('Error al cargar la póliza');
      navigate('/policies');
    } finally {
      setLoading(false);
    }
  };

  const fetchCommission = async () => {
    try {
      const response = await api.get(`/policies/${id}/commission`);
      setCommission(response.data);
    } catch (error) {
      console.error('Error al cargar comisión:', error);
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await api.get(`/policies/${id}/export-pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `policy_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF descargado exitosamente');
    } catch (error) {
      toast.error('Error al exportar PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Cargando...</div>
      </div>
    );
  }

  if (!policy) return null;

  return (
    <div>
      <Button
        data-testid="back-to-policies-button"
        variant="ghost"
        onClick={() => navigate('/policies')}
        className="mb-4 text-slate-600 hover:text-[#2537b0] hover:bg-blue-50"
      >
        <ArrowLeft size={20} className="mr-2" />
        Volver a Pólizas
      </Button>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {policy.policy_number}
          </h1>
          {policy.alias && (
            <p className="text-sm text-slate-500 mt-1 italic">
              Alias: {policy.alias}
            </p>
          )}
          <p className="text-sm leading-relaxed text-slate-600 mt-2" style={{ fontFamily: 'Inter, sans-serif' }}>
            {policy.insurer}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="export-pdf-button"
            onClick={handleExportPDF}
            variant="outline"
            className="border-[#e0e0e0] text-slate-700 hover:bg-slate-50"
          >
            <Download size={20} className="mr-2" />
            Exportar PDF
          </Button>
          <Button
            data-testid="edit-policy-button"
            onClick={() => navigate(`/policies/${id}/edit`)}
            className="bg-[#2537b0] hover:bg-[#1e2b8f] text-white"
          >
            <Edit size={20} className="mr-2" />
            Editar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-white border border-[#e0e0e0] rounded-[10px] shadow-sm p-6">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Información General
              </h2>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClasses(policy.status)}`}>
                {policy.status}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {policy.alias && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Alias</p>
                  <p className="text-sm font-medium text-slate-900">{policy.alias}</p>
                </div>
              )}
              {policy.policy_type && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Tipo</p>
                  <p className="text-sm font-medium text-slate-900">{policy.policy_type}</p>
                </div>
              )}
              {policy.plan && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Plan</p>
                  <p className="text-sm font-medium text-slate-900">{policy.plan}</p>
                </div>
              )}
              {policy.currency && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Moneda</p>
                  <p className="text-sm font-medium text-slate-900">{policy.currency}</p>
                </div>
              )}
            </div>
          </Card>

          <Card className="bg-white border border-[#e0e0e0] rounded-[10px] shadow-sm p-6">
            <h2 className="text-xl font-medium text-slate-800 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Fechas
            </h2>
            {(policy.emission_date || policy.start_date || policy.end_date) ? (
              <div className="grid grid-cols-2 gap-4">
                {(policy.emission_date || policy.start_date) && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Emisión</p>
                    <p className="text-sm font-medium text-slate-900">{formatDate(policy.emission_date || policy.start_date)}</p>
                  </div>
                )}
                {policy.end_date && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Vencimiento</p>
                    <p className="text-sm font-medium text-slate-900">{formatDate(policy.end_date)}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-500 italic">No hay fechas registradas</p>
            )}
          </Card>

          <Card className="bg-white border border-[#e0e0e0] rounded-[10px] shadow-sm p-6">
            <h2 className="text-xl font-medium text-slate-800 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Personas
            </h2>
            <div className="space-y-4">
              <div className="bg-blue-50 p-3 rounded">
                <p className="text-xs font-semibold uppercase tracking-wider text-blue-900 mb-2">📋 Contratante</p>
                <p className="text-sm font-medium text-slate-900">{policy.contractor?.name || policy.contractor}</p>
                {policy.contractor?.dob && (
                  <p className="text-xs text-slate-600 mt-1">Fecha de Nacimiento: {formatDate(policy.contractor.dob)}</p>
                )}
              </div>
              <div className="bg-green-50 p-3 rounded">
                <p className="text-xs font-semibold uppercase tracking-wider text-green-900 mb-2">🛡️ Asegurado Principal</p>
                <p className="text-sm font-medium text-slate-900">{policy.main_insured.name}</p>
                {policy.main_insured.dob && (
                  <p className="text-xs text-slate-600 mt-1">Fecha de Nacimiento: {formatDate(policy.main_insured.dob)}</p>
                )}
              </div>
            </div>
          </Card>

          {/* NOTA: Sección de Contacto y Dirección fue eliminada.
              Estos datos se obtienen del cliente vinculado a la póliza */}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card className="bg-white border border-[#e0e0e0] rounded-[10px] shadow-sm p-6">
            <h2 className="text-xl font-medium text-slate-800 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Información Financiera
            </h2>
            <div className="space-y-4">
              {policy.insured_amount > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Suma Asegurada</p>
                  <p className="text-2xl font-bold text-[#2537b0]">
                    {formatCurrency(policy.insured_amount, policy.currency)}
                  </p>
                </div>
              )}
              {policy.annual_premium > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Prima Anual</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {formatCurrency(policy.annual_premium, policy.currency)}
                  </p>
                </div>
              )}
              {policy.commission_percentage > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Comisión</p>
                  <p className="text-lg font-semibold text-[#10b981]">
                    {formatCurrency((policy.annual_premium * policy.commission_percentage) / 100, policy.currency)}
                  </p>
                  <p className="text-xs text-slate-500">({policy.commission_percentage}%)</p>
                </div>
              )}
              {policy.insured_amount === 0 && policy.annual_premium === 0 && (
                <p className="text-sm text-slate-500 italic">No hay información financiera disponible</p>
              )}
            </div>
          </Card>

          <Card className="bg-white border border-[#e0e0e0] rounded-[10px] shadow-sm p-6">
            <h2 className="text-xl font-medium text-slate-800 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Forma de Pago
            </h2>
            {policy.payment ? (
              <div className="space-y-3">
                {policy.payment.frequency && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Frecuencia</p>
                    <p className="text-sm font-medium text-slate-900">{policy.payment.frequency}</p>
                  </div>
                )}
                {policy.payment.method && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Método</p>
                    <p className="text-sm font-medium text-slate-900">{policy.payment.method}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-500 italic">No hay información de pago disponible</p>
            )}
          </Card>

          {/* Audit Trail Card */}
          {(policy?.last_modified_by || policy?.last_modified_at) && (
            <Card className="bg-blue-50 border border-blue-200 rounded-[10px] shadow-sm p-4">
              <h2 className="text-sm font-semibold text-slate-700 mb-3">📝 Última Modificación</h2>
              <div className="space-y-2 text-sm">
                {policy.last_modified_by && (
                  <div>
                    <span className="text-slate-500">Por:</span>{' '}
                    <span className="font-medium text-slate-900">{policy.last_modified_by}</span>
                  </div>
                )}
                {policy.last_modified_at && (
                  <div>
                    <span className="text-slate-500">Fecha:</span>{' '}
                    <span className="font-medium text-slate-900">
                      {new Date(policy.last_modified_at).toLocaleString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Commission Calculation Card - Solo para Admins */}
          {isAdmin && commission && (
            <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-[10px] shadow-sm p-6">
              <h2 className="text-xl font-medium text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                💰 Cálculo de Comisión
              </h2>
              
              {commission.status === 'CANCELADA - NO COMISIONA' ? (
                <div className="text-center py-4">
                  <p className="text-red-600 font-semibold">Póliza Cancelada - No Comisiona</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-white rounded-[10px] p-3 border border-emerald-100">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Año de Vigencia</p>
                    <p className="text-2xl font-bold text-[#2537b0]">Año {commission.vigencia_year}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-[10px] p-3 border border-emerald-100">
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Prima Anual</p>
                      <p className="text-sm font-bold text-slate-900">{formatCurrency(commission.prima_anual, policy.currency)}</p>
                    </div>
                    
                    <div className="bg-white rounded-[10px] p-3 border border-emerald-100">
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">IVA (16%)</p>
                      <p className="text-sm font-bold text-slate-900">{formatCurrency(commission.iva, policy.currency)}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {commission.policy_type.toUpperCase().includes('VIDA') ? 'Vida: 0% IVA' : '16% incluido'}
                      </p>
                    </div>
                  </div>

                  <div className="bg-white rounded-[10px] p-3 border border-emerald-100">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Prima Neta (Base)</p>
                    <p className="text-sm font-bold text-slate-900">{formatCurrency(commission.prima_neta, policy.currency)}</p>
                  </div>

                  <div className="bg-emerald-100 rounded-[10px] p-4 border-2 border-emerald-300">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Comisión Anual</p>
                    <p className="text-3xl font-bold text-[#10b981]">{formatCurrency(commission.comision_anual, policy.currency)}</p>
                    <p className="text-sm text-slate-600 mt-1">Porcentaje: {commission.porcentaje_comision}%</p>
                  </div>

                  {commission.frecuencia_pago && (
                    <div className="bg-white rounded-[10px] p-3 border border-emerald-100">
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Comisión por Pago ({commission.frecuencia_pago})</p>
                      <p className="text-xl font-bold text-[#2537b0]">{formatCurrency(commission.comision_por_pago, policy.currency)}</p>
                    </div>
                  )}
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
